import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { AsyncBoundary, Icon } from '@kollez/design-system';
import {
  useInterviews,
  useNotifCountsBySubject,
  type InterviewListItem,
  type InterviewsListOpts,
} from '@/lib/queries';
import { formatIndianDate, formatTime } from '@kollez/shared';
import { isOnlineInterviewMode, prettyRoundType } from '@kollez/shared/enums';

/**
 * /interviews — Student's own interview roster.
 *
 * Deep-audit rebuild (Page 4/N of the page-by-page pass):
 *   - Segments driven by Interview.status (schema-authoritative), not by
 *     "scheduledAt vs now" wall-time comparison. That fixes:
 *       * a cancelled interview that hasn't reached its scheduledAt time
 *         doesn't sit in Upcoming anymore.
 *       * a rescheduled OLD row is banished from Upcoming and appears with
 *         a "Moved" chip in the Changes segment; the NEW row shows normally.
 *   - Segment counts, search, mode filter, sort — all mapped to Pass 2
 *     canonical query params. Server pagination.
 *   - Countdown chip on the closest Upcoming row (24 h window: "in 3 h",
 *     "in 15 min", or "starting now" if scheduledAt is in the last 30 min).
 *   - Rescheduled-from chip when Interview.rescheduledFrom is populated.
 *   - Join button on online rows when start is within 30 min.
 *
 * Missing APIs check: none — every filter/sort/summary field is on the
 * Pass 2 /interviews list. `rescheduledFrom` + `rescheduleReason` are
 * returned by the server unfiltered.
 */

const UPCOMING = ['scheduled', 'in_progress'];
const PAST = ['completed', 'no_show'];
const CHANGES = ['rescheduled', 'cancelled'];

type Seg = 'up' | 'past' | 'changes';
type SortKey = NonNullable<InterviewsListOpts['sortBy']>;
const PAGE_SIZE = 50;

function humanCountdown(iso: string): { label: string; tone: 'danger' | 'warning' | 'info' | 'muted' } | null {
  const target = new Date(iso).getTime();
  const now = Date.now();
  const ms = target - now;
  if (!Number.isFinite(ms)) return null;
  const absMin = Math.abs(ms) / 60_000;
  if (ms < -30 * 60_000) return null;
  if (ms < 30 * 60_000 && ms > -30 * 60_000) return { label: 'Starting now', tone: 'danger' };
  if (absMin < 60) return { label: `in ${Math.round(absMin)} min`, tone: 'warning' };
  const hours = ms / 3_600_000;
  if (hours < 24) return { label: `in ${Math.round(hours)} h`, tone: 'warning' };
  const days = hours / 24;
  if (days < 7) return { label: `in ${Math.round(days)} d`, tone: 'info' };
  return null;
}

function statusChip(status: string, resultOnRow?: string, feedbackResult?: string): { label: string; tone: 'success' | 'warning' | 'info' | 'muted' | 'danger' } {
  const result = resultOnRow ?? feedbackResult;
  if (result === 'Selected') return { label: 'Cleared', tone: 'success' };
  if (result === 'Rejected') return { label: 'Not selected', tone: 'muted' };
  if (result === 'On_Hold') return { label: 'On hold', tone: 'warning' };
  if (result === 'Needs_Another_Round') return { label: 'Another round', tone: 'info' };
  if (status === 'cancelled') return { label: 'Cancelled', tone: 'muted' };
  if (status === 'rescheduled') return { label: 'Moved', tone: 'warning' };
  if (status === 'no_show') return { label: 'Missed', tone: 'danger' };
  if (status === 'in_progress') return { label: 'Live', tone: 'danger' };
  if (status === 'completed') return { label: 'Awaiting result', tone: 'info' };
  return { label: 'Scheduled', tone: 'info' };
}

function ModeIcon({ mode }: { mode: string }) {
  // Registry-safe fallbacks. Prefer glyphs that reliably exist in packages/design-system.
  const online = isOnlineInterviewMode(mode);
  return online
    ? <span aria-hidden style={{ fontSize: 11 }} title="Online">🖥</span>
    : <span aria-hidden style={{ fontSize: 11 }} title="In person">📍</span>;
}

/** Live re-render every 30s so the countdown / join button stay accurate. */
function useLiveTick() {
  const [_, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((v) => v + 1), 30_000);
    return () => clearInterval(id);
  }, []);
  void _;
}

export function StudentInterviewsPage() {
  const [seg, setSeg] = useState<Seg>('up');
  const [q, setQ] = useState('');
  const [modeFilter, setModeFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortKey>('scheduledAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  useLiveTick();

  const listQuery = useInterviews({
    page,
    limit: PAGE_SIZE,
    q: q.trim() || undefined,
    mode: modeFilter || undefined,
    sortBy,
    sortDir,
  });
  const rows = listQuery.data?.interviews ?? [];
  const summary = listQuery.data?.summary?.byStatus ?? {};

  const countFor = (statuses: string[]) => statuses.reduce((n, s) => n + (summary[s] ?? 0), 0);
  const upCount = countFor(UPCOMING);
  const pastCount = countFor(PAST);
  const changesCount = countFor(CHANGES);
  const totalCount = upCount + pastCount + changesCount;

  const rowsForSeg = useMemo(() => {
    const target = seg === 'up' ? UPCOMING : seg === 'past' ? PAST : CHANGES;
    return rows
      .filter((r) => target.includes(r.status))
      // Default order: soonest upcoming first / most recent past first.
      .sort((a, b) => {
        const av = new Date(a.scheduledAt).getTime();
        const bv = new Date(b.scheduledAt).getTime();
        return seg === 'up' ? av - bv : bv - av;
      });
  }, [rows, seg]);

  // Notification pill overlay (real endpoint) — keyed on visible rows only.
  const notifCounts = useNotifCountsBySubject('Interview', rowsForSeg.map((r) => r._id));
  const unreadById = notifCounts.data?.counts ?? {};

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Interviews</h1>
          <p className="subhead">
            {totalCount === 0
              ? "You'll see interviews here once you're shortlisted."
              : `${totalCount} total · ${upCount} upcoming · ${pastCount} completed${changesCount > 0 ? ` · ${changesCount} changes` : ''}`}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="segmented">
          <button aria-pressed={seg === 'up'} onClick={() => { setSeg('up'); setSortDir('asc'); setPage(1); }}>
            Upcoming <span className="helper ml-1">{upCount}</span>
          </button>
          <button aria-pressed={seg === 'past'} onClick={() => { setSeg('past'); setSortDir('desc'); setPage(1); }}>
            Past <span className="helper ml-1">{pastCount}</span>
          </button>
          <button aria-pressed={seg === 'changes'} onClick={() => { setSeg('changes'); setSortDir('desc'); setPage(1); }}>
            Changes <span className="helper ml-1">{changesCount}</span>
          </button>
        </div>
        <input
          type="search"
          className="input"
          placeholder="Search room or panel"
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }}
          style={{ minWidth: 200 }}
        />
        <select
          className="select"
          value={modeFilter}
          onChange={(e) => { setModeFilter(e.target.value); setPage(1); }}
        >
          <option value="">All modes</option>
          <option value="Online_Video">Online (video)</option>
          <option value="Online_Voice">Online (voice)</option>
          <option value="In_Person">In person</option>
          <option value="Hybrid">Hybrid</option>
        </select>
        <select
          className="select"
          value={sortBy}
          onChange={(e) => { setSortBy(e.target.value as SortKey); setPage(1); }}
        >
          <option value="scheduledAt">By schedule</option>
          <option value="roundNumber">By round</option>
          <option value="createdAt">Most recently added</option>
        </select>
      </div>

      <AsyncBoundary
        kind="table"
        loading={listQuery.isLoading}
        error={listQuery.isError ? (listQuery.error instanceof Error ? listQuery.error.message : true) : undefined}
        empty={!listQuery.isLoading && rowsForSeg.length === 0}
        emptyTitle={seg === 'up' ? 'No upcoming interviews' : seg === 'past' ? 'No past interviews' : 'No cancellations or moves'}
        emptyHint={seg === 'up' ? "You'll see interviews here once you're shortlisted." : undefined}
        onRetry={() => void listQuery.refetch()}
      >
        <div className="card">
          {rowsForSeg.map((r: InterviewListItem, idx) => {
            const co = r.partner?.name ?? r.drive?.driveName ?? 'Company';
            const round = r.roundType ? prettyRoundType(r.roundType) : `Round ${r.roundNumber}`;
            const isOnline = isOnlineInterviewMode(r.mode);
            const where = isOnline
              ? (r.meetingLink ? 'Online — link ready' : 'Online')
              : (r.room ?? r.drive?.college?.name ?? '—');
            const st = statusChip(r.status, r.result, r.feedback?.result);
            const cd = seg === 'up' && idx === 0 ? humanCountdown(r.scheduledAt) : null;
            const startMs = new Date(r.scheduledAt).getTime() - Date.now();
            const canJoinNow = isOnline && r.meetingLink && r.status === 'scheduled' && startMs < 30 * 60_000 && startMs > -60 * 60_000;
            const isReschedInstance = r.status === 'rescheduled' || Boolean(r.rescheduledFrom);

            return (
              <Link
                key={r._id}
                to={`/interviews/${r._id}`}
                className="app-row"
                style={{ gridTemplateColumns: '36px minmax(0,1fr) auto', textDecoration: 'none' }}
              >
                <span className="logo-tile" style={{ position: 'relative' }}>
                  {co.slice(0, 2).toUpperCase()}
                  {unreadById[r._id] ? (
                    <span
                      aria-label={`${unreadById[r._id]} unread`}
                      style={{
                        position: 'absolute', top: -4, right: -4, minWidth: 16, height: 16,
                        padding: '0 4px', borderRadius: 8, background: 'rgb(var(--ink-danger))',
                        color: 'rgb(var(--ink-inverse))', fontSize: 10, fontWeight: 700,
                        lineHeight: '16px', textAlign: 'center',
                      }}
                    >
                      {unreadById[r._id]! > 9 ? '9+' : unreadById[r._id]}
                    </span>
                  ) : null}
                </span>
                <div className="min-w-0">
                  <div className="cell-title truncate">
                    {co} · {round}
                    {isReschedInstance && r.status !== 'rescheduled' ? (
                      <span className="chip chip-warning ml-2" style={{ fontSize: 10 }} title={r.rescheduleReason ?? 'Rescheduled'}>Moved</span>
                    ) : null}
                  </div>
                  <div className="cell-sub truncate flex items-center gap-1">
                    <ModeIcon mode={r.mode} />
                    <span>{formatIndianDate(r.scheduledAt)} · {formatTime(r.scheduledAt)} · {where}</span>
                    {r.panel ? <span className="helper" style={{ marginLeft: 4 }}>· panel {r.panel}</span> : null}
                    {cd ? <span className={`chip chip-${cd.tone}`} style={{ marginLeft: 6, fontSize: 10 }}>{cd.label}</span> : null}
                  </div>
                </div>
                <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>
                  {canJoinNow && r.meetingLink ? (
                    <a
                      href={r.meetingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary btn-sm"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Join
                    </a>
                  ) : null}
                  <span className={`chip chip-${st.tone === 'info' ? 'info' : st.tone === 'success' ? 'success' : st.tone === 'warning' ? 'warning' : st.tone === 'danger' ? 'danger' : 'muted'}`}>
                    {st.label}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </AsyncBoundary>

      {(totalCount > PAGE_SIZE) && (
        <div className="flex items-center justify-between gap-2 mt-2">
          <span className="helper">Page {page}</span>
          <div className="flex gap-2">
            <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</button>
            <button className="btn btn-ghost btn-sm" disabled={rows.length < PAGE_SIZE} onClick={() => setPage((p) => p + 1)}>Next</button>
          </div>
        </div>
      )}

      {seg === 'up' && upCount > 0 && (
        <Link to="/mock" className="ai-suggest">
          <Icon name="mic" size={14} />
          <div className="flex-1">
            <b>An interview coming up?</b> A 12-minute Nexie mock takes you through DSA, DBMS and your project.
          </div>
        </Link>
      )}
    </div>
  );
}
